<template>
  <!-- This component renders social media meta tags in the document head -->
</template>

<script setup lang="ts">
import { computed, onMounted, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useStrapiStore } from '../stores/strapi'

interface Props {
  /**
   * Page-specific social meta data
   */
  pageSocials?: {
    ogTitle?: string | null
    ogDescription?: string | null
    ogImage?: {
      url?: string | null
      alternativeText?: string | null
    } | null
    twitterCard?: 'summary' | 'summary_large_image' | null
    twitterTitle?: string | null
    twitterDescription?: string | null
    twitterImage?: {
      url?: string | null
      alternativeText?: string | null
    } | null
  } | null
  /**
   * Page title for fallback
   */
  pageTitle?: string | null
  /**
   * Page description for fallback
   */
  pageDescription?: string | null
  /**
   * Page featured image for fallback
   */
  pageImage?: {
    url?: string | null
    alternativeText?: string | null
  } | null
  /**
   * Site name for title suffix
   */
  siteName?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  pageSocials: null,
  pageTitle: null,
  pageDescription: null,
  pageImage: null,
  siteName: 'Cross Works Ltd'
})

const route = useRoute()
const strapiStore = useStrapiStore()

// Computed properties for Open Graph data with fallbacks
const ogTitle = computed(() => {
  // Priority: page OG title -> page title -> global OG title -> site name
  const pageSocialsTitle = props.pageSocials?.ogTitle
  const globalSocialsTitle = strapiStore.globalSocials?.ogTitle
  
  if (pageSocialsTitle) return pageSocialsTitle
  if (props.pageTitle) return props.pageTitle
  if (globalSocialsTitle) return globalSocialsTitle
  
  return props.siteName || 'Cross Works Ltd'
})

const ogDescription = computed(() => {
  // Priority: page OG description -> page description -> global OG description -> site description
  const pageSocialsDescription = props.pageSocials?.ogDescription
  const globalSocialsDescription = strapiStore.globalSocials?.ogDescription
  const siteDescription = strapiStore.siteInfo?.description
  
  if (pageSocialsDescription) return pageSocialsDescription
  if (props.pageDescription) return props.pageDescription
  if (globalSocialsDescription) return globalSocialsDescription
  if (siteDescription) return siteDescription
  
  return ''
})

const ogImage = computed(() => {
  // Priority: page OG image -> page featured image -> global OG image
  const pageSocialsImage = props.pageSocials?.ogImage
  const globalSocialsImage = strapiStore.globalSocials?.ogImage
  
  if (pageSocialsImage?.url) return pageSocialsImage.url
  if (props.pageImage?.url) return props.pageImage.url
  if (globalSocialsImage?.url) return globalSocialsImage.url
  
  return null
})

const ogImageAlt = computed(() => {
  // Get alt text for the OG image
  const pageSocialsImage = props.pageSocials?.ogImage
  const globalSocialsImage = strapiStore.globalSocials?.ogImage
  
  if (pageSocialsImage?.alternativeText) return pageSocialsImage.alternativeText
  if (props.pageImage?.alternativeText) return props.pageImage.alternativeText
  if (globalSocialsImage?.alternativeText) return globalSocialsImage.alternativeText
  
  return ogTitle.value
})

const ogUrl = computed(() => {
  // Construct Open Graph URL from current route
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${route.fullPath}`
  }
  return ''
})

// Computed properties for Twitter Card data
const twitterCard = computed(() => {
  // Priority: page twitter card -> global twitter card -> default
  const pageSocialsCard = props.pageSocials?.twitterCard
  const globalSocialsCard = strapiStore.globalSocials?.twitterCard
  
  if (pageSocialsCard) return pageSocialsCard
  if (globalSocialsCard) return globalSocialsCard
  
  // Default to summary_large_image if we have an image, otherwise summary
  return ogImage.value ? 'summary_large_image' : 'summary'
})

const twitterTitle = computed(() => {
  // Priority: page twitter title -> page OG title -> page title -> global twitter title -> OG title
  const pageSocialsTwitterTitle = props.pageSocials?.twitterTitle
  const globalSocialsTwitterTitle = strapiStore.globalSocials?.twitterTitle
  
  if (pageSocialsTwitterTitle) return pageSocialsTwitterTitle
  if (globalSocialsTwitterTitle) return globalSocialsTwitterTitle
  
  // Fallback to OG title
  return ogTitle.value
})

const twitterDescription = computed(() => {
  // Priority: page twitter description -> page OG description -> global twitter description -> OG description
  const pageSocialsTwitterDescription = props.pageSocials?.twitterDescription
  const globalSocialsTwitterDescription = strapiStore.globalSocials?.twitterDescription
  
  if (pageSocialsTwitterDescription) return pageSocialsTwitterDescription
  if (globalSocialsTwitterDescription) return globalSocialsTwitterDescription
  
  // Fallback to OG description
  return ogDescription.value
})

const twitterImage = computed(() => {
  // Priority: page twitter image -> page OG image -> global twitter image -> OG image
  const pageSocialsTwitterImage = props.pageSocials?.twitterImage
  const globalSocialsTwitterImage = strapiStore.globalSocials?.twitterImage
  
  if (pageSocialsTwitterImage?.url) return pageSocialsTwitterImage.url
  if (globalSocialsTwitterImage?.url) return globalSocialsTwitterImage.url
  
  // Fallback to OG image
  return ogImage.value
})

// Function to update social media meta tags
const updateSocialMetaTags = () => {
  if (typeof document === 'undefined') return

  // Helper function to update or create meta tag
  const updateMetaTag = (property: string, content: string) => {
    if (!content) return
    
    let element = document.querySelector(`meta[property="${property}"]`)
    
    if (!element) {
      element = document.createElement('meta')
      element.setAttribute('property', property)
      document.head.appendChild(element)
    }
    
    element.setAttribute('content', content)
  }

  // Helper function for Twitter meta tags (uses name instead of property)
  const updateTwitterMetaTag = (name: string, content: string) => {
    if (!content) return
    
    let element = document.querySelector(`meta[name="${name}"]`)
    
    if (!element) {
      element = document.createElement('meta')
      element.setAttribute('name', name)
      document.head.appendChild(element)
    }
    
    element.setAttribute('content', content)
  }

  // Update Open Graph meta tags
  updateMetaTag('og:type', 'website')
  updateMetaTag('og:site_name', props.siteName || 'Cross Works Ltd')
  
  const ogTitleValue = ogTitle.value
  if (ogTitleValue) updateMetaTag('og:title', ogTitleValue)
  
  const ogDescValue = ogDescription.value
  if (ogDescValue) updateMetaTag('og:description', ogDescValue)
  
  const ogUrlValue = ogUrl.value
  if (ogUrlValue) updateMetaTag('og:url', ogUrlValue)
  
  const ogImageValue = ogImage.value
  if (ogImageValue) {
    updateMetaTag('og:image', ogImageValue)
    
    const ogImageAltValue = ogImageAlt.value
    if (ogImageAltValue) updateMetaTag('og:image:alt', ogImageAltValue)
  }

  // Update Twitter Card meta tags
  const twitterCardValue = twitterCard.value
  if (twitterCardValue) updateTwitterMetaTag('twitter:card', twitterCardValue)
  
  const twitterTitleValue = twitterTitle.value
  if (twitterTitleValue) updateTwitterMetaTag('twitter:title', twitterTitleValue)
  
  const twitterDescValue = twitterDescription.value
  if (twitterDescValue) updateTwitterMetaTag('twitter:description', twitterDescValue)
  
  const twitterImageValue = twitterImage.value
  if (twitterImageValue) updateTwitterMetaTag('twitter:image', twitterImageValue)
}

// Watch for changes and update meta tags
watchEffect(() => {
  updateSocialMetaTags()
})

// Initialize meta tags on mount
onMounted(() => {
  updateSocialMetaTags()
})
</script> 